<template>
  <Loading
    :loading="taskLoading"
    loading-text="Loading tasks..."
  >
    <div class="task-table-container ">
      <div class="table-content">
        <TaskTableTab :activeTaskTab="activeTaskTab" @switch-tab="handleTabChange" @sort-change="handleSortChange"/>
        <TaskTableDetail 
          :task-data="pagedTaskData" 
          :activeTaskTab="activeTaskTab" 
          @open-create-task="emit('open-create-task')"
        />
      </div>
      <div id="pagination-bar">
        <div data-testid="my-calendar.task-table.showing-count-text" class="w-[220px]">Showing {{ startTaskIdx }} to {{ endTaskIdx }} of {{ filteredTaskData.length }} tasks</div>
        <Pagination
          :total-num="filteredTaskData.length"
          :cur-page="curPage"
          :per-page="perPage"
          @page-change="handlePageChange"
        />
        <div class="w-[200px]">
          <SingleSelector
            data-testid="my-calendar.task-table.per-page-select"
            :options="paginationOptions" 
            size="medium"
            :model-value="perPage"
            @update:model-value="handlePerPageChange"
          />
        </div>
      </div>
    </div>
  </Loading>
</template>

<script setup>
import TaskTableTab from './TaskTableTab.vue'
import TaskTableDetail from './TaskTableDetail.vue'
import Pagination from '@/views/my-calendar/components/Pagination.vue'
import Loading from '@/NewLibrary/Loading/Loading.vue';
import { paginationOptions } from '@/views/my-calendar/mock-data'
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import moment from 'moment'


const { taskData, activeTaskTab } = defineProps(['taskData', 'activeTaskTab'])

const emit = defineEmits(['open-create-task'])

const filterTab = ref('all')
const perPage = ref(10)
const curPage = ref(1)

const store = useStore()
const taskLoading = computed(() => store.state.calendar.taskLoading)

const sortOrder = ref('due-desc')
const filteredTaskData = computed(() => {
  let filtered = []
  if (filterTab.value === 'all') {
    filtered = taskData
  } else if (filterTab.value === 'completed') {
    filtered = taskData.filter(task => task.is_completed === true)
  } else if (filterTab.value === 'today') {
    const today = moment();
    filtered = taskData.filter(task => {
      if (!task.due_date) return false;
      const createdAt = moment(task.due_date);
      return createdAt.isSame(today, 'day');
    });
  } else if (filterTab.value === 'upcoming') {
    const endOfToday = moment().endOf('day');
    filtered = taskData.filter(task => {
      if (!task.due_date) return false;
      const createdAt = moment(task.due_date);
      return createdAt.isAfter(endOfToday);
    });
  } else {
    filtered = taskData
  }

  // Sorting by due date
  return [...filtered].sort((a, b) => {
    const dateA = a.due_date ? moment(a.due_date) : moment(0)
    const dateB = b.due_date ? moment(b.due_date) : moment(0)
    if (sortOrder.value === 'due-asc') {
      return dateA - dateB
    } else if (sortOrder.value === 'due-desc') {
      return dateB - dateA
    }
    return 0
  })
})

// Pagination Data
const pagedTaskData = computed(() =>
  filteredTaskData.value.slice((curPage.value - 1) * perPage.value, curPage.value * perPage.value)
)

const startTaskIdx = computed(() => {
  if (!filteredTaskData.value.length) return 0;
  const start = (curPage.value - 1) * perPage.value + 1;
  return Math.min(start, filteredTaskData.value.length);
})
const endTaskIdx = computed(() => {
  if (!filteredTaskData.value.length) return 0;
  const end = curPage.value * perPage.value;
  return Math.min(end, filteredTaskData.value.length);
})

function handleTabChange(tab) {
  filterTab.value = tab
  curPage.value = 1 
}

function handlePageChange(newPage) {
  curPage.value = newPage
}

function handlePerPageChange(newPerPage) {
  perPage.value = newPerPage
  curPage.value = 1 
}

function handleSortChange(value) {
  sortOrder.value = value
  // console.log(value)
}

watch([filterTab, perPage], () => {
  curPage.value = 1
})
</script>

<style scoped>
.task-table-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  flex: 1 1 auto;
  min-height: 0;
  padding-left: 36px;
  padding-right: 36px;
}
.switch-tab {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 2px 6px;
  cursor: pointer;
  color: #666666;
  font-weight: 500;
}
.switch-tab.active {
  color: #5587BC;
}
.table-content {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto; 
  overflow-x: hidden;
  padding-bottom: 12px;
}
#pagination-bar {
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin:10px 0px 0px ;
  position: sticky;
  bottom: 0;
  background: #ffffff;
  z-index: 10;
  caret-color: transparent;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
}
</style>
